import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {AgGridReact} from 'ag-grid-react';
import {AllCommunityModule, ModuleRegistry} from 'ag-grid-community';
import {gaiaTheme} from '@vsn-ux/gaia-styles/integrations/ag-grid';
import type {
    CellClickedEvent,
    ColDef,
    ColumnMovedEvent,
    ColumnResizedEvent,
    FocusGridInnerElementParams,
    GetRowIdParams,
    GridApi,
    GridReadyEvent,
    ICellRendererParams,
    IRowNode,
    IsFullWidthRowParams,
    PostSortRowsParams,
    ProcessCellForExportParams,
    ProcessRowParams,
    RowClassParams,
    RowHeightParams,
    ShouldRowBeSkippedParams,
    ValueFormatterParams,
    ValueGetterParams
} from 'ag-grid-community';
import {CheckIcon, Info, SquareChartGantt, TriangleAlert, XIcon} from 'lucide-react';
import Checkbox from 'components/common/checkbox.component';
import {Button, ButtonType} from 'components/common/Button.component';
import {ICON_SIZE} from 'components/common/constants';
import {ROW_TOOLTIP_PLACE, SHARED_TOOLTIP_ID} from 'components/tooltip/AppTooltip.component';
import {Tooltip} from 'components/tooltip/Tooltip';
import {DOCUMENT_TYPE, TASK_ACTIONS, TASK_OVERDUE_STATUS} from 'utils/constants';
import {taskDetailsPath} from 'components/router/detailsPath';
import {formatDocumentType, getTaskOverdueStatus} from 'utils/valueFormatter.function';
import {handleNPKeyNavigation} from 'utils/keyPressHandler.function';
import {TaskCommentsCell} from './TaskCommentsCell.component';
import TaskQuickView from './TaskQuickView.component';
import {MyTasksActions} from './AvailableActions.class';
import {canReassignReview} from './tasksOnReview.function';
import {formatTotal, normalizeTaskAmount} from './taskAmounts.function';
import {
    CELL_TYPE,
    CSV_UNIT_COLUMNS,
    FIELD_META,
    FROM_FIELD,
    ROW_TYPE,
    STATE_FIELD,
    buildRowData,
    csvColumnIds,
    csvFileName,
    csvSafeValue,
    csvValue,
    displayValue,
    fieldLabel,
    getRowId,
    orderVisibleFields,
    regroupSortedNodes,
    sortValue
} from './tasksGrid.function';
import {
    bodyTabStops,
    headerCellControl,
    pageStopOutside,
    renderedRows,
    rowIndexOf,
    rowTabStops
} from './tasksGridFocus.function';
import type {ColumnLayout, GridRow, SortedColumn} from './tasksGrid.function';
import type {TaskGroup} from './tasksGrouping.function';
import type {MyTask, TaskWrapper, Translate} from 'types/myTasks';
import './tasksGrid.scss';

// ag-grid v33+ requires explicit module registration; Community only, so no licence key
ModuleRegistry.registerModules([AllCommunityModule]);

// the group row toggles on click, except on these: the checkbox selects the group's tasks and
// Approve all acts on them
const GROUP_CONTROLS = '.tasks-grid__group-select, .tasks-grid__group-approve';

const SELECT_COL = 'select';
const ACTIONS_COL = 'actions';
const CSV_UNIT_COL_IDS: readonly string[] = Object.freeze(Object.values(CSV_UNIT_COLUMNS).map(column => column.colId));

// Rows are named apart from the flat list's so a test can tell the two renderings apart, while the
// cells keep the flat list's names so the same assertion reads either one.
// ag-grid builds the row and its plain text cells itself, so they are stamped on afterwards -
// see processRowPostCreate below. Cells rendered by this file carry their own data-cy inline.
const ROW_DATA_CY = Object.freeze({task: 'myTasks.task.row', onReview: 'myTasks.onReview.row', group: 'myTasks.group.row'});
const rowDataCyOf = (isTask: boolean, isOnReview: boolean): string => {
    if (isOnReview) return ROW_DATA_CY.onReview;
    return isTask ? ROW_DATA_CY.task : ROW_DATA_CY.group;
};
const CELL_DATA_CY: Readonly<Record<string, string>> = Object.freeze({
    [FROM_FIELD]: 'myTasks.task.supplierName',
    description: 'myTasks.task.description',
    companyName: 'myTasks.task.companyName'
});

// the columns whose text reads over two lines rather than being cut at the first (see tasksGrid.scss)
const WRAPPING_FIELDS: readonly string[] = Object.freeze(['description', STATE_FIELD]);

const ROW_HEIGHT = Object.freeze({
    // as tight as the two-line cells (clamped text, amount over its foreign one) still fit in
    task: 44,
    group: 44,
    header: 40
});

// the list opens on the latest document due date; the grid owns its sorting from there on
const DEFAULT_SORT = Object.freeze({colId: 'documentDueDate', sort: 'desc' as const});

// the action bar is fixed to the bottom, so the grid has to stop above it - 4px short of the
// bar's own 60, which it can reach into without touching the buttons
const ACTION_BAR_HEIGHT = 56;
const MIN_GRID_HEIGHT = 200;

// a row tabbed to from outside the rendered window is only drawn a frame or two after the scroll
const FOCUS_RENDER_ATTEMPTS = 5;

interface TasksGridProps {
    groups: TaskGroup[];
    collapsedGroups?: Record<string, boolean>;
    visibleFields: string[];
    // the order and widths the user last dragged the columns into; null = as they come
    columnLayout?: ColumnLayout | null;
    onColumnLayoutChange?: (layout: ColumnLayout) => void;
    // the columns the list is sorted by; null = the sort the list opens on
    sortState?: SortedColumn[] | null;
    onSortStateChange?: (sorted: SortedColumn[]) => void;
    // the task rows in the order the grid shows them, reported on every change to it
    onSortedKeysChange?: (keys: string[]) => void;
    translate: Translate;
    // the Export button sits in the page's filter bar, so the grid publishes its CSV export here
    exportRef?: React.MutableRefObject<(() => void) | null>;
    allSelected?: boolean;
    onToggleAll: () => void;
    onToggleTask: (key: string) => void;
    onToggleGroup: (groupKey: string) => void;
    onToggleGroupSelection: (wrappers: TaskWrapper[]) => void;
    onApproveGroup: (wrappers: TaskWrapper[]) => void;
    taskAction: (action: string, keys: string[], options?: {hasDraftComment?: boolean}) => void;
    // the eye button opening the quick view drawer
    quickViewEnabled?: boolean;
    // hand a review the user sent over to another reviewer
    onReassignReview?: (task: MyTask) => void;
}

/**
 * The grid fills whatever is left below it: the payment batch and the hidden-tasks warning
 * push it down when they show, and their heights are not known up front.
 */
function useAvailableHeight(elementRef: React.RefObject<HTMLElement>): {top: number; height: number} | null {
    const [bounds, setBounds] = useState<{top: number; height: number} | null>(null);

    useLayoutEffect(() => {
        const measure = () => {
            const element = elementRef.current;
            if (!element) return;
            const top = element.getBoundingClientRect().top;
            setBounds({top, height: Math.max(MIN_GRID_HEIGHT, window.innerHeight - top - ACTION_BAR_HEIGHT)});
        };

        measure();
        window.addEventListener('resize', measure);
        // the blocks above the grid live in the page container, not in the grid's own parent:
        // watching it is what catches them showing and hiding, so this subscribes once
        const container = elementRef.current?.closest('.ast_task_list');
        const observer = container && typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(measure)
            : null;
        observer?.observe(container);

        return () => {
            window.removeEventListener('resize', measure);
            observer?.disconnect();
        };
    }, [elementRef]);

    return bounds;
}

/**
 * Gaia's own ag-grid theme, with this list's deviations on top.
 *
 * Gaia expresses its sizes in rem against a 16px root, while this app runs on a 10px one -
 * which is what --ga-base-scaling-factor (1.6, set by all-10pt.css) is for. Gaia's stylesheet
 * multiplies by it, so every size taken straight from a token has to do the same.
 */
const gaiaSize = (token: string) => `calc(var(${token}) * var(--ga-base-scaling-factor, 1))`;

const gridTheme = gaiaTheme.withParams({
    // Gaia's own sizes, scaled to this app's root
    fontSize: gaiaSize('--ga-text-md-font-size'),
    dataFontSize: gaiaSize('--ga-text-md-font-size'),
    // the heading reads at the same size as the values under it, only heavier
    headerFontSize: gaiaSize('--ga-text-md-font-size'),
    cellHorizontalPadding: gaiaSize('--ga-size-spacing-04'),
    iconSize: gaiaSize('--ga-size-16'),
    wrapperBorderRadius: gaiaSize('--ga-size-8'),

    // the list reads as one plain surface; Gaia's theme would tint every other row
    oddRowBackgroundColor: 'var(--ga-color-white)',

    // Gaia's own row colours and corner. These were this list's choices before it took Gaia's
    // theme; kept here commented in case any of them reads better than what Gaia specifies
    // rowHoverColor: 'var(--ga-color-surface-action-hover-2)',
    // selectedRowBackgroundColor: 'var(--ga-color-surface-selected)',
    // wrapperBorderRadius: '4px',

    // the dark bubble the shared Gaia tooltip uses everywhere else (see AppTooltip.component)
    tooltipBackgroundColor: 'var(--ga-color-surface-action-hover)',
    tooltipTextColor: 'var(--ga-color-text-on-action)',
    tooltipBorder: false
});

/**
 * ag-grid rendering of the my-tasks list.
 *
 * Grouping is drawn with full-width rows rather than ag-grid row grouping, which is an
 * Enterprise-only feature.
 */
const TasksGrid = ({
                       groups,
                       collapsedGroups,
                       visibleFields,
                       columnLayout,
                       onColumnLayoutChange,
                       sortState,
                       onSortStateChange,
                       onSortedKeysChange,
                       translate,
                       exportRef,
                       allSelected,
                       onToggleAll,
                       onToggleTask,
                       onToggleGroup,
                       onToggleGroupSelection,
                       onApproveGroup,
                       taskAction,
                       quickViewEnabled = false,
                       onReassignReview
                   }: TasksGridProps) => {

    const navigate = useNavigate();
    const fields = useMemo(
        () => orderVisibleFields(visibleFields, columnLayout?.order),
        [visibleFields, columnLayout]
    );

    const gridRef = useRef<HTMLDivElement>(null);
    const gridApiRef = useRef<GridApi<GridRow> | null>(null);

    // only the sort the grid opens on: from there the grid owns it, and hands changes back up
    const openingSortRef = useRef(sortState);
    const openingSort = useMemo(() => {
        // a stored sort on a column since switched off cannot be applied, and the list opens sorted anyway
        const stored = (openingSortRef.current ?? []).filter(entry => fields.includes(entry.colId));
        const opening = stored.length > 0 ? stored : [DEFAULT_SORT];
        return new Map<string, SortedColumn>(opening.map(entry => [entry.colId, entry] as [string, SortedColumn]));
    }, [fields]);
    // the task the quick view drawer is showing, none when it is closed
    const [quickViewTask, setQuickViewTask] = useState<MyTask | null>(null);

    const rowData = useMemo(
        () => buildRowData(groups, collapsedGroups, translate),
        [groups, collapsedGroups, translate]
    );

    const selectRenderer = useCallback((params: ICellRendererParams<GridRow>) => {
        if (params.data?.rowType !== ROW_TYPE.TASK) return null;
        const task = params.data.task;
        // a review the user sent is no task of theirs to act on in bulk
        if (task.isOnReview) return null;
        // the checkbox's own label fills the cell (see tasksGrid.scss), so a click anywhere in
        // the cell reaches the box natively - no handler of ours, and no second tab stop
        return (
            <Checkbox
                inputId={'task-checkbox-' + task.id}
                onChange={(event) => {
                    event.stopPropagation();
                    onToggleTask(task.key);
                }}
                isChecked={Boolean(params.data.selected)}
                isDisabled={task.isHandled}
                dataCy="myTasks.task.select"
                ariaLabel={translate('myTasks.task.selectTaskOf', DOCUMENT_TYPE.asString(task.documentType), task.companyName || '')}
            />
        );
    }, [onToggleTask, translate]);

    // its label fills the header cell too, as in the rows below it
    const selectAllHeader = useCallback(() => (
        <Checkbox
            inputId="tasks-grid-select-all"
            onChange={onToggleAll}
            isChecked={Boolean(allSelected)}
            dataCy="myTasks.selectAll"
            ariaLabel={translate('myTasks.selectAllTasks')}
        />
    ), [allSelected, onToggleAll, translate]);

    const documentTypeRenderer = useCallback((params: ICellRendererParams<GridRow>) => {
        const task = params.data?.task;
        if (!task) return null;
        return (
            <span className={'document-type-icon-24 ' + formatDocumentType(task.documentType, task.amount) + '-24'}
                  title={translate('documentType.' + DOCUMENT_TYPE.asString(task.documentType))}
                  data-cy="myTasks.task.type"/>
        );
    }, [translate]);

    const dueDateRenderer = useCallback((params: ICellRendererParams<GridRow>) => {
        const task = params.data?.task;
        if (!task) return null;
        const status = getTaskOverdueStatus(task.documentDueDate);
        const overdue = status.overdueClass === TASK_OVERDUE_STATUS.TASK_OVERDUE;
        return (
            <span className="tasks-grid__due" data-cy="myTasks.task.documentDueDate">
                {overdue &&
                    <TriangleAlert size={ICON_SIZE.SMALL} className="me-1"
                                   aria-label={translate('myTasks.task.taskOverdue', status.dueDate, params.value)}
                                   style={{color: 'var(--ga-color-icon-warning)'}}/>
                }
                {params.valueFormatted ?? params.value}
            </span>
        );
    }, [translate]);

    const amountRenderer = useCallback((params: ICellRendererParams<GridRow>) => {
        const task = params.data?.task;
        if (!task) return null;
        const amounts = normalizeTaskAmount(task, translate);
        return (
            <span className="tasks-grid__amount">
                <span className="tasks-grid__amount-main">
                    <span data-cy="myTasks.task.amount">{amounts.amount}</span>
                    <span className="tasks-grid__currency" data-cy="myTasks.task.currency">{amounts.unit}</span>
                </span>
                {amounts.foreignAmount &&
                    <span className="tasks-grid__foreign" data-cy="myTasks.task.foreignAmount">
                        {amounts.foreignAmount} {amounts.foreignCurrency}
                    </span>
                }
            </span>
        );
    }, [translate]);

    // the same figure the amount cell shows on its second line, in a column of its own: the
    // currency belongs next to it there too, so the number is not read as the main one
    const foreignAmountRenderer = useCallback((params: ICellRendererParams<GridRow>) => {
        const task = params.data?.task;
        if (!task) return null;
        const amounts = normalizeTaskAmount(task, translate);
        if (!amounts.foreignAmount) return null;
        return (
            <span className="tasks-grid__amount">
                <span className="tasks-grid__amount-main">
                    <span>{amounts.foreignAmount}</span>
                    <span className="tasks-grid__currency">{amounts.foreignCurrency}</span>
                </span>
            </span>
        );
    }, [translate]);

    // WRAPPING_FIELDS read over two lines, and the clamp needs an element of its own: the cell
    // has to stay a flex box for one line to sit centred and the second to grow from it
    const wrappingRenderer = useCallback((field: string) => (params: ICellRendererParams<GridRow>) => {
        const task = params.data?.task;
        if (!task) return null;
        const text = displayValue(task, field, translate);
        if (!text) return null;
        return <span className="tasks-grid__wrap">{text}</span>;
    }, [translate]);

    const commentsRenderer = useCallback((params: ICellRendererParams<GridRow>) => {
        const task = params.data?.task;
        if (!task) return null;
        if (!task.numberOfComments && !task.hasDraftComment) return null;
        return <TaskCommentsCell task={task} translate={translate}/>;
    }, [translate]);

    const actionsRenderer = useCallback((params: ICellRendererParams<GridRow>) => {
        const task = params.data?.task;
        if (!task) return null;

        // all the sender can do with a review is give it to another reviewer
        if (task.isOnReview) {
            return (
                <span className="tasks-grid__actions">
                    {onReassignReview && canReassignReview(task) &&
                        <Button
                            type={ButtonType.SECONDARY}
                            onClick={() => onReassignReview(task)}
                            dataCy="myTasks.onReview.reassign"
                            text={translate('myTasks.onReview.reassign')}
                        />
                    }
                </span>
            );
        }

        if (task.isHandled) {
            const tooltipId = 'task-handled-tooltip-' + task.id;
            return (
                <span className="tasks-grid__actions tasks-grid__handled"
                      data-tip={tooltipId} data-for={SHARED_TOOLTIP_ID} data-place={ROW_TOOLTIP_PLACE}>
                    <Tooltip id={tooltipId} content={() => translate('myTasks.taskWasHandled')}/>
                    <Info size={ICON_SIZE.MEDIUM} className="handled-info"
                          aria-label={translate('myTasks.taskWasHandled')}/>
                </span>
            );
        }

        const actions = new MyTasksActions(task);
        // review replaces the quick actions, exactly as the flat list did
        const reviewRow = actions.isEnabled(TASK_ACTIONS.REVIEW);

        return (
            <span className="tasks-grid__actions">
                {quickViewEnabled &&
                    <Button
                        type={ButtonType.GHOST}
                        onClick={() => setQuickViewTask(task)}
                        iconOnly={true}
                        icon={<SquareChartGantt size={ICON_SIZE.MEDIUM}/>}
                        ariaLabel={translate('myTasks.overview.quickView.open')}
                        dataCy="myTasks.task.quickView"
                        extraClassName="tasks-grid__quick-view"
                    />
                }
                {reviewRow ?
                    <Button
                        type={ButtonType.SECONDARY}
                        onClick={() => taskAction(TASK_ACTIONS.REVIEW, [task.key])}
                        dataCy={'task.action.' + translate('myTasks.actionBar.review')}
                        text={translate('myTasks.actionBar.review')}
                    /> :
                    <>
                        {task.taskActions?.includes('quick-approve') &&
                            <Button
                                type={ButtonType.GHOST}
                                onClick={() => taskAction(TASK_ACTIONS.APPROVE, [task.key], {hasDraftComment: task.hasDraftComment})}
                                iconOnly={true}
                                icon={<CheckIcon size={ICON_SIZE.MEDIUM}/>}
                                ariaLabel={translate('myTasks.task.approve')}
                                dataCy={'task.action.' + translate('myTasks.task.approve')}
                            />
                        }
                        {task.taskActions?.includes('quick-reject') &&
                            <Button
                                type={ButtonType.GHOST}
                                onClick={() => taskAction(TASK_ACTIONS.REJECT, [task.key])}
                                iconOnly={true}
                                icon={<XIcon size={ICON_SIZE.MEDIUM}/>}
                                ariaLabel={translate('myTasks.task.reject')}
                                dataCy={'task.action.' + translate('myTasks.task.reject')}
                            />
                        }
                    </>
                }
            </span>
        );
    }, [taskAction, translate, quickViewEnabled, onReassignReview]);

    const RENDERERS: Record<string, (params: ICellRendererParams<GridRow>) => React.ReactElement | null> =
        useMemo(() => ({
            documentType: documentTypeRenderer,
            description: wrappingRenderer('description'),
            [STATE_FIELD]: wrappingRenderer(STATE_FIELD),
            amount: amountRenderer,
            foreignAmount: foreignAmountRenderer,
            numberOfComments: commentsRenderer,
            documentDueDate: dueDateRenderer
        }), [documentTypeRenderer, wrappingRenderer, amountRenderer, foreignAmountRenderer,
            commentsRenderer, dueDateRenderer]);

    const columnDefs = useMemo<ColDef<GridRow>[]>(() => {
        const dataColumn = (field: string): ColDef<GridRow> => {
            const meta = FIELD_META[field];
            const isNumeric = meta?.type === CELL_TYPE.AMOUNT || meta?.type === CELL_TYPE.NUMBER;
            const isDate = meta?.type === CELL_TYPE.DATE || meta?.type === CELL_TYPE.DATETIME;
            const defaultWidth = meta?.width ?? 140;
            const width = columnLayout?.widths[field] ?? defaultWidth;
            const renderer = RENDERERS[field];
            return {
                colId: field,
                headerName: fieldLabel(field, translate),
                // a narrowed column clips its heading; the tooltip is what spells it out again
                headerTooltip: fieldLabel(field, translate),
                width,
                // a column narrower than the shared minimum keeps the width FIELD_META asked for
                minWidth: Math.min(80, defaultWidth),
                type: isNumeric ? 'rightAligned' : undefined,
                cellClass: isDate ? 'tasks-grid__date-cell' : undefined,
                // "initial" so it only seeds the opening sort: a plain `sort` would come back
                // on every column rebuild and overrule whatever the user sorted by
                initialSort: openingSort.get(field)?.sort,
                initialSortIndex: openingSort.size > 1 ? openingSort.get(field)?.sortIndex : undefined,
                // the row is a wrapper, so every value is read off `data.task`
                valueGetter: (params: ValueGetterParams<GridRow>) => sortValue(params.data?.task, field),
                valueFormatter: (params: ValueFormatterParams<GridRow>) => displayValue(params.data?.task, field, translate),
                cellRenderer: renderer,
                // ag-grid cannot spot truncation inside a cell renderer, so such a column would show
                // its tooltip on every hover; the doc type icon carries its own title, the rest read
                // fine as they are
                // the clamped columns are the exception: their text is cut often enough to want it
                tooltipValueGetter: renderer && !WRAPPING_FIELDS.includes(field)
                    ? undefined
                    : (params) => displayValue(params.data?.task, field, translate)
            };
        };

        const scrollingColumns = fields.map(dataColumn);

        // never shown: they only exist so the CSV export has a column to write the units into
        const csvUnitColumns: ColDef<GridRow>[] = Object.values(CSV_UNIT_COLUMNS).map(column => ({
            colId: column.colId,
            headerName: translate(column.labelKey),
            hide: true,
            lockVisible: true,
            sortable: false,
            suppressMovable: true
        }));

        return [
            {
                colId: SELECT_COL,
                headerName: '',
                width: 44,
                minWidth: 44,
                maxWidth: 44,
                pinned: 'left',
                sortable: false,
                resizable: false,
                suppressMovable: true,
                cellClass: 'tasks-grid__select-cell',
                headerClass: 'tasks-grid__select-header',
                cellRenderer: selectRenderer,
                headerComponent: selectAllHeader,
                headerTooltip: translate('myTasks.selectAllTasks')
            },
            ...scrollingColumns,
            {
                colId: ACTIONS_COL,
                headerName: translate('myTasks.column.action'),
                headerTooltip: translate('myTasks.column.action'),
                width: 10,
                // the Review button's label is translated, so the column has to fit the longest one
                minWidth: quickViewEnabled ? 160 : 120,
                pinned: 'right',
                sortable: false,
                suppressMovable: true,
                cellClass: 'tasks-grid__actions-cell',
                cellRenderer: actionsRenderer
            },
            ...csvUnitColumns
        ];
    }, [fields, columnLayout, openingSort, translate, RENDERERS, selectRenderer, selectAllHeader, actionsRenderer, quickViewEnabled]);

    const groupRowRenderer = useCallback((params: ICellRendererParams<GridRow>) => {
        const row = params.data;
        const groupSelected = row.wrappers.length > 0 && row.wrappers.every(wrapper => wrapper.selected);
        // the whole header row toggles the group, so the controls that mean something else
        // have to opt out
        const toggleGroup = (event: React.MouseEvent<HTMLDivElement>) => {
            if ((event.target as HTMLElement).closest(GROUP_CONTROLS)) return;
            onToggleGroup(row.groupKey);
        };
        return (
            <div className={'tasks-grid__group' + (row.collapsed ? ' collapsed' : '')} data-cy="myTasks.group"
                 onClick={toggleGroup}>
                <span className="tasks-grid__group-select">
                    {/* the reviews cannot be selected, so neither can their group - the cell keeps the title aligned */}
                    {!row.onReview &&
                        <Checkbox
                            inputId={'group-checkbox-' + row.groupKey}
                            onChange={() => onToggleGroupSelection(row.wrappers)}
                            isChecked={groupSelected}
                            dataCy="myTasks.group.select"
                            ariaLabel={translate('myTasks.overview.selectGroup', row.label)}
                        />
                    }
                </span>
                <button type="button"
                        className={'tasks-grid__group-toggle' + (row.collapsed ? ' collapsed' : '')}
                        aria-expanded={!row.collapsed}
                        data-cy="myTasks.group.toggle">
                    <span className="tasks-grid__group-chevron" aria-hidden="true"/>
                    <span className="tasks-grid__group-title">{row.label}</span>
                    <span className="ga-badge ga-badge--text ga-badge--muted">{row.count}</span>
                </button>
                <span className="tasks-grid__group-total">{row.totals.map(formatTotal).join(', ')}</span>
                {/* group-level approve all is hidden for now; the plumbing stays for when it comes back
                {!row.collapsed && row.approvable.length > 0 &&
                    <Button
                        type={ButtonType.GHOST}
                        onClick={() => onApproveGroup(row.wrappers)}
                        extraClassName="tasks-grid__group-approve"
                        dataCy="myTasks.group.approveAll"
                        text={translate('myTasks.overview.approveAll')}
                    />
                }
                */}
            </div>
        );
    }, [onToggleGroup, onToggleGroupSelection, onApproveGroup, translate]);

    /**
     * ag-grid owns the row element and its plain cells, so the hooks the e2e suite needs are
     * added here - the callback ag-grid documents for exactly this. Every row passes through it
     * as it is created, so filtering and sorting keep them in place.
     */
    const processRowPostCreate = useCallback((params: ProcessRowParams<GridRow>) => {
        const isTask = params.node.data?.rowType === ROW_TYPE.TASK;
        const isOnReview = Boolean(isTask && params.node.data.task.isOnReview);
        params.eRow.setAttribute('data-cy', rowDataCyOf(isTask, isOnReview));
        if (!isTask) return;
        // the row is the stop that opens the task, and carries the tab index from the moment it
        // exists - ag-grid adds its own a frame later, by when the tab has moved on. A handled task
        // cannot be opened, so its row stays out, as in the flat list - and so does a review the
        // user sent, which is not theirs to open
        params.eRow.setAttribute('tabindex', params.node.data.task.isHandled || isOnReview ? '-1' : '0');
        for (const [colId, dataCy] of Object.entries(CELL_DATA_CY)) {
            params.eRow.querySelector('[col-id="' + colId + '"]')?.setAttribute('data-cy', dataCy);
        }
    }, []);

    /**
     * The classes that follow the row's state. getRowClass is read once, when the row is built,
     * so a class it hands out is never taken back - these are re-evaluated as the data changes.
     */
    const quickViewKey = quickViewTask?.key;
    const rowClassRules = useMemo(() => {
        const isSelected = (params: RowClassParams<GridRow>) => Boolean(params.data?.selected);
        return {
            'tasks-grid__row--selected': isSelected,
            // the marker the flat list uses, so one selector serves both versions
            'selected-row': isSelected,
            'tasks-grid__row--handled': (params: RowClassParams<GridRow>) => Boolean(params.data?.task?.isHandled),
            'tasks-grid__row--new': (params: RowClassParams<GridRow>) => Boolean(params.data?.task?.isNew),
            'tasks-grid__row--on-review': (params: RowClassParams<GridRow>) => Boolean(params.data?.task?.isOnReview),
            // ag-grid re-applies the rules when they change, so this follows the drawer
            'tasks-grid__row--quick-view': (params: RowClassParams<GridRow>) => (
                Boolean(quickViewKey) && params.data?.task?.key === quickViewKey
            )
        };
    }, [quickViewKey]);

    // the details page's next/previous keys, walking the tasks in the order the grid shows them
    useEffect(() => {
        if (!quickViewKey) return;
        // the helper's delayed move must not land after the drawer closed or moved on
        let isCancelled = false;

        const move = (fromKey: string, isForward: boolean) => {
            const api = gridApiRef.current;
            if (isCancelled || !api) return;
            const nodes: IRowNode<GridRow>[] = [];
            api.forEachNodeAfterFilterAndSort(node => {
                if (node.data?.task) nodes.push(node);
            });
            const index = nodes.findIndex(node => node.data?.task?.key === fromKey);
            if (index === -1) return;
            // the open task stays the anchor even if it was handled meanwhile
            const step = isForward ? 1 : -1;
            let next: IRowNode<GridRow> | undefined;
            for (let i = index + step; i >= 0 && i < nodes.length; i += step) {
                if (!nodes[i].data?.task?.isHandled) {
                    next = nodes[i];
                    break;
                }
            }
            if (!next?.data?.task) return;
            setQuickViewTask(next.data.task);
            api.ensureNodeVisible(next);
        };

        const onKeyDown = (event: KeyboardEvent) => {
            // Shift stays allowed: N and P navigate in either case
            if (event.ctrlKey || event.metaKey || event.altKey) return;
            handleNPKeyNavigation(event, quickViewKey, move);
        };
        document.addEventListener('keydown', onKeyDown);
        return () => {
            isCancelled = true;
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [quickViewKey]);

    const groupOrder = useMemo(() => groups.map(group => group.key), [groups]);

    const postSortRows = useCallback(
        (params: PostSortRowsParams<GridRow>) => regroupSortedNodes(params.nodes, groupOrder),
        [groupOrder]
    );

    const gridBounds = useAvailableHeight(gridRef);

    /**
     * hands the columns' current order and widths up to be stored. A column currently switched
     * off is not in the grid to report a width, so the widths already known are kept.
     */
    const persistLayout = useCallback(() => {
        const api = gridApiRef.current;
        if (!api || !onColumnLayoutChange) return;

        const order: string[] = [];
        const widths: Record<string, number> = {...columnLayout?.widths};
        for (const column of api.getColumnState()) {
            // the two pinned edge columns are the grid's own, not the user's to arrange
            if (column.colId === SELECT_COL || column.colId === ACTIONS_COL) continue;
            if (CSV_UNIT_COL_IDS.includes(column.colId)) continue;
            order.push(column.colId);
            if (column.width) widths[column.colId] = column.width;
        }
        onColumnLayoutChange({order, widths});
    }, [columnLayout, onColumnLayoutChange]);

    // what the grid shows, as CSV: the data columns in the order and sort the user put them in
    const exportCsv = useCallback(() => {
        const api = gridApiRef.current;
        if (!api) return;

        // the select and action columns hold controls, not data
        const displayed = api.getAllDisplayedColumns()
            .map(column => column.getColId())
            .filter(colId => colId !== SELECT_COL && colId !== ACTIONS_COL);
        const columns = csvColumnIds(displayed);

        api.exportDataAsCsv({
            fileName: csvFileName(),
            columnKeys: columns,
            // one flat table of tasks: a group heading would only be a line of empty cells to
            // every reader the file is opened in
            // the reviews the user sent are someone else's tasks, so they are left out as well
            shouldRowBeSkipped: (params: ShouldRowBeSkippedParams<GridRow>) =>
                params.node.data?.rowType === ROW_TYPE.GROUP || Boolean(params.node.data?.task?.isOnReview),
            // a callback of our own switches the columns' value formatters off, so the printable
            // value is taken here as well
            processCellCallback: (params: ProcessCellForExportParams<GridRow>) =>
                csvSafeValue(csvValue(params.node?.data?.task, params.column.getColId(), translate))
        });
    }, [translate]);

    // the button lives outside the grid, in the filter bar, so its handle is handed up
    useEffect(() => {
        if (!exportRef) return;
        exportRef.current = exportCsv;
        return () => {
            exportRef.current = null;
        };
    }, [exportRef, exportCsv]);

    // the sort last handed up, so an unchanged one is not stored again on every model update
    const reportedSortRef = useRef<string>('');

    /**
     * What the grid currently shows: the columns it is sorted by, and its task rows in the order
     * they read in. Reported from every model update, which is what sorting, filtering and
     * collapsing a group all end in.
     */
    const reportGridState = useCallback(() => {
        const api = gridApiRef.current;
        if (!api) return;

        if (onSortStateChange) {
            const sorted = api.getColumnState()
                .filter(column => column.sort)
                .sort((first, second) => (first.sortIndex ?? 0) - (second.sortIndex ?? 0))
                .map(column => ({
                    colId: column.colId,
                    sort: column.sort as 'asc' | 'desc',
                    sortIndex: column.sortIndex ?? undefined
                }));
            const asReported = JSON.stringify(sorted);
            if (asReported !== reportedSortRef.current) {
                reportedSortRef.current = asReported;
                onSortStateChange(sorted);
            }
        }

        if (onSortedKeysChange) {
            const keys: string[] = [];
            api.forEachNodeAfterFilterAndSort(node => {
                // stepping from task to task only walks the user's own
                if (node.data?.rowType === ROW_TYPE.TASK && !node.data.task.isOnReview) keys.push(node.data.task.key);
            });
            onSortedKeysChange(keys);
        }
    }, [onSortStateChange, onSortedKeysChange]);

    const openTask = useCallback((task?: MyTask) => {
        // a review the user sent has no key to open it by
        if (!task || task.isHandled || task.isOnReview) return;
        navigate(taskDetailsPath(task));
    }, [navigate]);

    const onCellClicked = useCallback((event: CellClickedEvent<GridRow>) => {
        if (event.data?.rowType !== ROW_TYPE.TASK) return;
        if (event.column.getColId() === SELECT_COL || event.column.getColId() === ACTIONS_COL) return;
        openTask(event.data.task);
    }, [openTask]);

    // the row is a tab stop of its own, and opens the task the way a click on it does
    const onRowKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
        const target = event.target as HTMLElement;
        if (event.key !== 'Enter' || !target.classList?.contains('tasks-grid__task-row')) return;
        openTask(gridApiRef.current?.getDisplayedRowAtIndex(rowIndexOf(target))?.data?.task);
    }, [openTask]);

    // focus one end of a row, scrolling to it first: a row outside the rendered window is only
    // drawn a frame or two after the scroll
    const focusRow = useCallback((index: number, backwards: boolean, attempts = FOCUS_RENDER_ATTEMPTS): boolean => {
        const root = gridRef.current;
        const api = gridApiRef.current;
        if (!root || !api || index < 0 || index >= api.getDisplayedRowCount()) return false;

        // a handled task holds no stop at all - its row is not navigable and its controls are
        // disabled - and nor does a review the user may not reassign, so the walk carries on past them
        const task = api.getDisplayedRowAtIndex(index)?.data?.task;
        const holdsNoStop = task?.isHandled || (task?.isOnReview && !(onReassignReview && canReassignReview(task)));
        if (holdsNoStop) return focusRow(index + (backwards ? -1 : 1), backwards, attempts);

        api.ensureIndexVisible(index);
        const row = renderedRows(root).find(candidate => rowIndexOf(candidate) === index);
        const stops = row ? rowTabStops(row) : [];
        if (stops.length > 0) {
            (backwards ? stops[stops.length - 1] : stops[0]).focus();
            return true;
        }
        // the row is still being drawn: scrolling to it, and filling its cells, takes a frame
        if (attempts > 0) requestAnimationFrame(() => focusRow(index, backwards, attempts - 1));
        return true;
    }, [onReassignReview]);

    // shift+tab in from the page below: ag-grid's tab guard would jump to the header, over every row
    const focusGridInnerElement = useCallback(
        (params: FocusGridInnerElementParams<GridRow>) => (
            params.fromBottom && focusRow(params.api.getDisplayedRowCount() - 1, true)
        ),
        [focusRow]
    );

    /**
     * Tab order: the header cells, then row by row - the row itself, then the controls it holds
     * (see tasksGridFocus.function). ag-grid gives the rows no stops of its own while cell focus is
     * suppressed, so the walk is driven here, in the capture phase ahead of ag-grid's own handlers,
     * and the event is only stopped for the steps taken over.
     */
    useEffect(() => {
        const root = gridRef.current;
        if (!root) return;

        const focusStop = (stop: HTMLElement | undefined, event: KeyboardEvent): boolean => {
            if (!stop) return false;
            event.preventDefault();
            event.stopPropagation();
            stop.focus();
            return true;
        };

        const onKeyDown = (event: KeyboardEvent) => {
            const api = gridApiRef.current;
            const target = event.target as HTMLElement | null;
            if (event.key !== 'Tab' || event.ctrlKey || event.altKey || event.metaKey) return;
            if (!api || !target?.closest) return;

            const backwards = event.shiftKey;
            const columns = api.getAllDisplayedColumns();
            const lastColumn = columns[columns.length - 1];

            const headerCell = target.closest<HTMLElement>('.ag-header-cell');
            if (headerCell) {
                // ag-grid walks the header cells itself; what it does not do is step into the
                // controls they hold - the select-all checkbox - or on into the rows
                if (target === headerCell && focusStop(headerCellControl(root, columns, headerCell, backwards), event)) return;
                if (backwards || headerCell.getAttribute('col-id') !== lastColumn?.getColId()) return;
                if (focusRow(0, false)) {
                    event.preventDefault();
                    event.stopPropagation();
                }
                return;
            }

            const stops = bodyTabStops(root);
            const current = stops.indexOf(target);
            if (current === -1) return;
            if (focusStop(stops[current + (backwards ? -1 : 1)], event)) return;

            // the end of the rendered rows: the next row has to be scrolled into view first
            const rows = renderedRows(root);
            const edge = backwards ? rows[0] : rows[rows.length - 1];
            if (edge && focusRow(rowIndexOf(edge) + (backwards ? -1 : 1), backwards)) {
                event.preventDefault();
                event.stopPropagation();
                return;
            }

            // the list ends here: back into the header, or on to the page below the grid
            if (backwards && lastColumn) {
                event.preventDefault();
                event.stopPropagation();
                api.setFocusedHeader(lastColumn);
                return;
            }
            focusStop(pageStopOutside(root, backwards), event);
        };

        root.addEventListener('keydown', onKeyDown, true);
        return () => root.removeEventListener('keydown', onKeyDown, true);
    }, [focusRow]);

    return (
        <>
        {/* the drawer does not block the list, so the list makes room for it rather than sliding
            under it - ag-grid picks up the narrower container on its own */}
        <div className={'tasks-grid' + (quickViewTask ? ' aw:pr-[var(--app-drawer-width,420px)]' : '')}
             data-cy="myTasks.grid" ref={gridRef} onKeyDown={onRowKeyDown}
             style={gridBounds ? {height: gridBounds.height} : undefined}>
            <AgGridReact<GridRow>
                theme={gridTheme}
                onGridReady={(event: GridReadyEvent<GridRow>) => (gridApiRef.current = event.api)}
                focusGridInnerElement={focusGridInnerElement}
                rowData={rowData}
                columnDefs={columnDefs}
                getRowId={(params: GetRowIdParams<GridRow>) => getRowId(params.data)}
                headerHeight={ROW_HEIGHT.header}
                getRowHeight={(params: RowHeightParams<GridRow>) => (params.data?.rowType === ROW_TYPE.GROUP ? ROW_HEIGHT.group : ROW_HEIGHT.task)}
                isFullWidthRow={(params: IsFullWidthRowParams<GridRow>) => params.rowNode.data?.rowType === ROW_TYPE.GROUP}
                fullWidthCellRenderer={groupRowRenderer}
                getRowClass={(params: RowClassParams<GridRow>) => (
                    // what the row is; what it is currently in is in rowClassRules, and the
                    // data-cy hooks are stamped on in processRowPostCreate
                    params.data?.task ? 'tasks-grid__task-row' : 'tasks-grid__group-row'
                )}
                rowClassRules={rowClassRules}
                onCellClicked={onCellClicked}
                postSortRows={postSortRows}
                onModelUpdated={reportGridState}
                processRowPostCreate={processRowPostCreate}
                // a column scrolled out of view is still rendered, so its cell can be read
                // without scrolling the grid to it first. The column set is a fixed, bounded
                // list (FIELD_META), so this stays a handful of extra cells per row
                suppressColumnVirtualisation={true}
                // lockPinned: a column dragged over a pinned edge would pin itself there, and the
                // stored layout has no room for that
                defaultColDef={{sortable: true, resizable: true, lockPinned: true}}
                // only the drag the user just let go of: the grid fires both of these while it
                // lays itself out, and resizing fires on every pixel of the drag
                onColumnResized={(event: ColumnResizedEvent<GridRow>) => {
                    if (event.finished && event.source === 'uiColumnResized') persistLayout();
                }}
                onColumnMoved={(event: ColumnMovedEvent<GridRow>) => {
                    if (event.finished && event.source === 'uiColumnMoved') persistLayout();
                }}
                suppressCellFocus={true}
                // the tab order walks the rows in display order, so the DOM has to be in that
                // order too - for the reader as much as for the fallback the browser provides
                ensureDomOrder={true}
                tooltipShowDelay={300}
                // the values are readable in the cell; only a clipped one needs spelling out
                tooltipShowMode="whenTruncated"
                localeText={{noRowsToShow: translate('myTasks.noTasksFound')}}
                domLayout="normal"
            />
        </div>
        {/* outside the grid: its own tab handling walks the rows, and the drawer traps focus */}
        {quickViewTask &&
            <TaskQuickView
                task={quickViewTask}
                translate={translate}
                taskAction={taskAction}
                onClose={() => setQuickViewTask(null)}
                bounds={gridBounds}
            />
        }
        </>
    );
};

export default TasksGrid;
